import React, { useState } from 'react';
import { Modal, ModalContent, ModalCloseButton, Button } from '../common';
import SessionTemplateForm from './SessionTemplateForm';
import { normalizePegBoardQueueReturn } from '../../utils/pegBoard/constants';

export const sessionToEditFormData = (session) => {
  if (!session) return null;

  return {
    session_type_id: session.session_type_id || session.session_types?.id || '',
    session_name: session.name || session.club_session_setup?.session_name || '',
    name: session.name || session.club_session_setup?.session_name || '',
    description: session.description || '',
    max_players: session.max_players ?? '',
    number_of_courts: session.number_of_courts ?? '',
    price_per_player: session.price_per_player || '',
    court_booking_cost: session.court_booking_cost || '',
    location: session.location || '',
    min_elo_level: session.min_elo_level || '',
    start_time: session.start_time || '',
    session_time: session.start_time || '',
    end_time: session.end_time || '',
    session_date: session.session_date || '',
    day_of_week: session.day_of_week || '0',
    is_recurring: session.is_recurring || false,
    is_active: session.is_active !== false,
    signup_deadline_hours: session.signup_deadline_hours || '',
    waitlist_enabled: session.waitlist_enabled !== false,
    waitlist_promotion_type: session.waitlist_promotion_type || 'first_come_first_served',
    signup_selection: session.signup_selection || 'first_come',
    is_public: session.is_public || false,
    currency_code: session.currency_code || 'GBP',
    session_status: session.session_status || 'upcoming',
    game_type_restriction: session.game_type_restriction || 'all_types',
    enable_player_categorisation: session.enable_player_categorisation || false,
    competitive_match_format: session.competitive_match_format || 'doubles',
    points_target: session.points_target ?? 21,
    deuce_mode: session.deuce_mode || 'standard',
    peg_board_selection_window: session.peg_board_selection_window ?? 6,
    peg_board_queue_return: normalizePegBoardQueueReturn(session.peg_board_queue_return),
    session_series_id: session.session_series_id || '',
  };
};

const EditSessionModal = ({
  isOpen,
  onClose,
  session,
  selectedClub,
  clubId,
  currentUser,
  onSessionUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const resolvedClubId = clubId || selectedClub?.id || session?.club_id || null;

  const handleSessionUpdated = async (preparedData) => {
    setLoading(true);
    try {
      // Call the parent's onEditSession function with the session ID and prepared data
      if (onSessionUpdated) {
        await onSessionUpdated(session.id, preparedData);
      }
      onClose();
    } catch (error) {
      console.error('Error updating session:', error);
      // You might want to show an error message here
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !session) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent
        size="large"
        padding={false}
        className="flex max-h-[90vh] flex-col overflow-hidden border border-border-medium shadow-xl"
      >
        {/* Fixed Header */}
        <div className="relative flex shrink-0 items-center border-b border-border-medium px-6 py-4 pr-14">
          <h2 className="text-xl font-semibold text-text-primary">Edit Session</h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Scrollable Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          <SessionTemplateForm
            onSubmit={handleSessionUpdated}
            onCancel={onClose}
            initialData={sessionToEditFormData(session)}
            loading={loading}
            clubId={resolvedClubId}
            isModal={true}
            mode="session"
            creationMode="customize"
            hideActions={true}
          />
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border-medium bg-surface-subtle px-6 py-4">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            size="medium"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="session-template-form"
            variant="primary"
            size="medium"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Update Session'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default EditSessionModal;
